import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  isoDateLocal,
  validateStayDates,
  type StayDatesSelection,
} from '../lib/onboarding';
interface DateInputsFieldProps {
  stayStart: string;
  stayEnd: string;
  onChange: (start: string, end: string) => void;
  /** Validation error from the parent. Pass null when valid. */
  error: string | null;
}
/**
 * Pure controlled component — two native date inputs and an optional
 * error row. Parent owns state and validation. Used directly inside the
 * settings page where it sits alongside other fields under one Save
 * button.
 *
 * Field-label i18n (14 Jun 2026, Phase 4b-final-A) — "Arriving" /
 * "Heading off" route through t() with nested `dateInputs.*` keys in
 * the common namespace. The shared lib `src/lib/onboarding.ts` stays
 * pure (no React/i18n imports). Validation error messages from
 * validateStayDates still pass through as English strings on the
 * `error` prop; their i18n is deferred to Phase 4b-final-B which
 * changes the lib API to return codes.
 */
export function DateInputsField({
  stayStart,
  stayEnd,
  onChange,
  error,
}: DateInputsFieldProps) {
  const { t } = useTranslation('common');
  const startId = useId();
  const endId = useId();
  const errorId = useId();
  const todayStr = isoDateLocal(new Date());
  const inputCls =
    'rounded-lg border border-moor-200 bg-cream-50 px-3 py-2 text-base text-moor-900 focus:border-moor-500 focus:outline-none focus:ring-2 focus:ring-moor-500/40';
  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-3 sm:flex-row sm:gap-4">
        <label htmlFor={startId} className="flex flex-1 flex-col gap-1">
          <span className="text-xs font-medium uppercase tracking-wide text-moor-500">
            {t('dateInputs.arriving')}
          </span>
          <input
            id={startId}
            type="date"
            value={stayStart}
            min={todayStr}
            onChange={(e) => onChange(e.target.value, stayEnd)}
            aria-invalid={error !== null}
            {...(error !== null ? { 'aria-describedby': errorId } : {})}
            className={inputCls}
          />
        </label>
        <label htmlFor={endId} className="flex flex-1 flex-col gap-1">
          <span className="text-xs font-medium uppercase tracking-wide text-moor-500">
            {t('dateInputs.headingOff')}
          </span>
          <input
            id={endId}
            type="date"
            value={stayEnd}
            min={stayStart || todayStr}
            onChange={(e) => onChange(stayStart, e.target.value)}
            aria-invalid={error !== null}
            {...(error !== null ? { 'aria-describedby': errorId } : {})}
            className={inputCls}
          />
        </label>
      </div>
      {error !== null && (
        <p id={errorId} role="alert" className="text-sm text-red-700">
          {error}
        </p>
      )}
    </div>
  );
}
interface OnboardingDateStepProps {
  onSelect: (selection: StayDatesSelection) => void;
}
/**
 * Onboarding wrapper — owns local state for arrival/departure, runs
 * `validateStayDates` on every change, renders a Done button that emits
 * the selection only when valid. Used in the chat surface in place of
 * QuickReplies for the dates step.
 *
 * Done button i18n (14 Jun 2026, Phase 4b-final-A) — translated via
 * t('dateInputs.done'). DateInputsField's "Arriving" / "Heading off"
 * labels are already translated upstream so this entire step renders
 * in the guest's language across all four supported locales.
 */
export function OnboardingDateStep({ onSelect }: OnboardingDateStepProps) {
  const { t } = useTranslation('common');
  const todayStr = isoDateLocal(new Date());
  const weekLater = isoDateLocal(new Date(Date.now() + 7 * 86_400_000));
  const [stayStart, setStayStart] = useState(todayStr);
  const [stayEnd, setStayEnd] = useState(weekLater);
  const error = validateStayDates(stayStart, stayEnd);
  const canSubmit = error === null;
  return (
    <div className="flex flex-col gap-3 px-3 py-3">
      <DateInputsField
        stayStart={stayStart}
        stayEnd={stayEnd}
        onChange={(s, e) => {
          setStayStart(s);
          setStayEnd(e);
        }}
        error={error}
      />
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => {
            if (canSubmit) onSelect({ stayStart, stayEnd });
          }}
          disabled={!canSubmit}
          className="rounded-full bg-moor-700 px-5 py-2 text-sm font-medium text-cream-50 transition-colors hover:bg-moor-800 disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-moor-500/40 focus-visible:ring-offset-2"
        >
          {t('dateInputs.done')}
        </button>
      </div>
    </div>
  );
}
